import { Component, OnInit, OnDestroy, Inject, ViewChild } from '@angular/core';

import { AngularFirestore, AngularFirestoreCollection } from '@angular/fire/compat/firestore';
import { Observable, Subscription } from 'rxjs';
import { map, take, catchError } from 'rxjs/operators';

import { SelectionModel } from '@angular/cdk/collections';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';

import { ConfigurationService } from '../../configuration.service';
import { ProjectLanguage } from '../../firestore-models/project-language';
import { Hall } from '../../firestore-models/hall';
import { ApiHall } from '../../api-client-module';

import { AppConst } from '../../app-const';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-select-halls-dialog',
  templateUrl: './select-halls-dialog.component.html',
  styleUrls: ['./select-halls-dialog.component.css'],
  standalone: false,
})
export class SelectHallsDialogComponent implements OnInit, OnDestroy {

  projectLanguages!: Array<ProjectLanguage>;

  private hallsCollection!: AngularFirestoreCollection<Hall>;
  hallsObservable!: Observable<Hall[]>;
  hallsSubscribe!: Subscription;
  projectId: string;

  displayedColumns: string[] = ['position', 'select', 'hall_id', 'show_paid_content', 'name'];
  dataSource = new MatTableDataSource<Hall>();
  selection = new SelectionModel<Hall>(true, []);

  searchFilter: any = {};

  projectBaseLang: string = 'ja';

  @ViewChild(MatPaginator, {static: true}) paginator!: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort!: MatSort;

  constructor(
    public dialogRef: MatDialogRef<SelectHallsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private angularFirestore: AngularFirestore,
    private configurationService: ConfigurationService,
    private translate: TranslateService
  ) {
      this.projectId = this.configurationService.getProjectId();
    }

  ngOnInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.setCustomFilter();
    this.projectLanguages = this.configurationService.getProject().languages!;
    // カテゴリ・建物情報取得
    this.hallsCollection = this.angularFirestore.collection<Hall>(
      AppConst.FIRESTORE_HALLS, ref => ref.where('project_id', '==', this.projectId).orderBy('hall_id', 'asc'));
    this.hallsObservable = this.hallsCollection.valueChanges({ idField: 'id' });
    this.hallsSubscribe = this.hallsObservable.subscribe(halls => {
      this.dataSource.data = this.addFilterValue(halls);
    });
    this.projectBaseLang = this.configurationService.getProject().project_base_lang;
  }

  ngOnDestroy(): void {
    this.hallsSubscribe.unsubscribe();
  }

  selectFunc(row: Hall) {
    const startDate: any = row!.sales_start_date ? new Date(row!.sales_start_date.seconds * 1000) : null;
    const endDate: any = row!.sales_end_date ? new Date(row!.sales_end_date.seconds * 1000) : null;
    const hall: ApiHall = {
      id: row.id,
      project_id: row.project_id,
      hall_id: row.hall_id,
      hall_explain: row.hall_explain,
      hall_names: row.hall_names,
      latitude: row.hall_geopoint.latitude,
      longitude: row.hall_geopoint.longitude,
      show_marker: row.show_marker,
      menu_disp_type: row.menu_disp_type,
      menu_disp_map_range: row.menu_disp_map_range,
      show_paid_content: row.show_paid_content,
      hall_descs: row.hall_descs,
      sales_start_date: startDate,
      sales_end_date: endDate,
      validity_period: row.validity_period,
      price: row.price,
      use_custom_menu: row.use_custom_menu,
      use_custom_menu_icon: row.use_custom_menu_icon,
      use_custom_side_icon: row.use_custom_side_icon,
      use_custom_side_image: row.use_custom_side_image,
      custom_menu_icon_file: row.custom_menu_icon_file,
      custom_side_icon_file: row.custom_side_icon_file,
      menu_icon: row.menu_icon,
      side_icon: row.side_icon,
      enabled: row.enabled,
    };
    this.dialogRef.close(hall);
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  sortData(sort: Sort) {
    const data = this.dataSource.data.slice();
    if (!sort.active || sort.direction === '') {
      this.dataSource.data = data;
      return;
    }
    const compare = (a: number | string, b: number | string, isAsc: boolean) => {
      return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
    }

    this.dataSource.data = data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'hall_id':
          return compare(a.hall_id, b.hall_id, isAsc);
        case 'name':
          const aName = a.hall_names.filter((d) => { return d.lang_code == this.projectLanguages[0].lang_code})[0].name;
          const bName = b.hall_names.filter((d) => { return d.lang_code == this.projectLanguages[0].lang_code})[0].name;
          return compare(aName, bName, isAsc);
        default:
          return 0;
      }
    });
  }

  setCustomFilter() {
    // NOTE: AND検索
    this.dataSource.filterPredicate = (data: any, filter: any) => {
      let matchCount = 0;
      let keys = Object.keys(filter);
      for (const key of keys) {
        if (!filter[key]) {
          // フィルタに値がない場合は全抽出
          matchCount++;
        } else {
          if (data[key].trim().toLowerCase().indexOf(filter[key]) !== -1) {
            matchCount++;
          }
        }
      }
      return (keys.length === matchCount);
    };
  }

  addFilterValue(docs: Array<any>) :Array<any> {
    for (const doc of docs) {
      let filterValue = '';
      for (const value of doc.hall_names) {
        filterValue += value.name + ' '
      }
      doc['filterValue'] = filterValue;
    }
    return docs;
  }

  applyFilter(event: Event) {
    this.selection.clear();
    const filterValue = (event.target as HTMLInputElement).value;
    this.searchFilter['filterValue'] = filterValue.trim().toLowerCase();
    this.dataSource.filter = this.searchFilter;
  }
}
